import { NativeAudioDevice } from '../services/ElectronIPC'
import { AudioEngineAudioDeviceInfo } from './AudioEngine'
import { logger } from '../services/Logger'

const log = logger.createChildLogger({ name: 'AudioMediaSourceMatcher' })

type RuleString = {
    chrome: string
    candidates: string | string[]
}

type Rule = {
    chrome: RegExp
    candidates: RegExp[]

    // for logging
    chrome_str: string
    candidates_str: string[]
}

export type MatchedMediaDeviceInfo = MediaDeviceInfo & {
    nativeDevice: NativeAudioDevice | null
    audioEngineDevice: AudioEngineAudioDeviceInfo | null
}

export default class AudioMediaSourceMatcher {
    ruleStrings: RuleString[] = []
    rules: Rule[] = []

    constructor() {
        this.ruleStrings = [
            { chrome: '.*AirPods.*', candidates: '.*AirPods.*' },

            { chrome: '.*Microphone \\(Built-in\\)', candidates: ['Built-in Microphone', 'Built-in Input'] },
            { chrome: '.*Line In \\(Built-in\\)', candidates: ['Built-in Line Input', 'Built-in Input'] },
            {
                chrome: '.*Digital In \\(Built-in\\)',
                candidates: ['Built-in Digital Input', 'Built-in Line Input', 'Built-in Input'],
            },

            { chrome: '.*Speakers \\(Built-in\\)', candidates: ['Built-in Speaker', 'Built-in Output'] },
            { chrome: '.*Headphones \\(Built-in\\)', candidates: ['Built-in Headphone', 'Built-in Output'] },
            { chrome: '.*Line Out \\(Built-in\\)', candidates: ['Built-in Line Output', 'Built-in Output'] },
            {
                chrome: '.*Digital Out \\(Built-in\\)',
                candidates: ['Built-in Digital Output', 'Built-in Line Output', 'Built-in Output'],
            },

            { chrome: 'Sound Booster \\(Virtual\\)', candidates: 'Sound Booster Driver' },
            {
                chrome: 'Meta Quest Remote Desktop Audio \\(Virtual\\)',
                candidates: 'Meta Quest Remote Desktop Audio Device',
            },
            { chrome: '.*Iriun Mic #0.*', candidates: 'Iriun Webcam Audio' },
        ]

        this.rules = this.ruleStrings.map((rule) => {
            let candidates: string[] = []
            if (typeof rule.candidates === 'string') {
                candidates = [rule.candidates]
            } else {
                candidates = rule.candidates
            }

            return {
                chrome: new RegExp(rule.chrome),
                candidates: candidates.map((str) => new RegExp(str)),

                chrome_str: rule.chrome,
                candidates_str: candidates,
            }
        })
    }

    run(
        _chromeDevices: MediaDeviceInfo[],
        _nativeDevices: NativeAudioDevice[],
        _audioEngineDevices: AudioEngineAudioDeviceInfo[]
    ): [MatchedMediaDeviceInfo[], string[]] {
        // we will be decorating `chromeDevices` with `nativeDevice` and `audioEngineDevice` properties.
        let chromeDevices = _chromeDevices.map((a) => {
            let b = a as MatchedMediaDeviceInfo
            b.nativeDevice = null
            b.audioEngineDevice = null
            return b
        })

        try {
            let nativeDevices = [..._nativeDevices]
            let audioEngineDevices = [..._audioEngineDevices]
            let errors: string[] = []

            // 1st pass: simple label matching
            for (let chromeDevice of chromeDevices) {
                if (nativeDevices.length > 0) {
                    let [result1, err1] = this.matchChromeDevice<NativeAudioDevice>(chromeDevice, nativeDevices)
                    if (err1) {
                        errors.push(err1)
                    }
                    if (result1) {
                        chromeDevice.nativeDevice = result1
                    }
                }

                if (audioEngineDevices.length > 0) {
                    const nativeDevice = chromeDevice.nativeDevice
                    if (nativeDevice != null) {
                        const match = audioEngineDevices.find((a) => a.deviceUid === nativeDevice.deviceUid)
                        if (match) {
                            chromeDevice.audioEngineDevice = match
                        }
                    }

                    if (!chromeDevice.audioEngineDevice) {
                        let [result2, err2] = this.matchChromeDevice<AudioEngineAudioDeviceInfo>(
                            chromeDevice,
                            audioEngineDevices
                        )
                        if (err2) {
                            errors.push(err2)
                        }
                        if (result2) {
                            chromeDevice.audioEngineDevice = result2
                        }
                    }
                }
            }
            return [chromeDevices, errors]
        } catch (e) {
            log.error('chromeDeviceLabelMatching', `exception: ${(e as any).message}`, { e })
            return [chromeDevices, ['Unexpected error occurred while matching devices.']]
        }
    }

    private matchChromeDevice<T extends NativeAudioDevice | AudioEngineAudioDeviceInfo>(
        chromeDevice: MediaDeviceInfo,
        candidates: T[]
    ): [T | null, string | null] {
        // inital match: the most basic case is when the label of the candidate is
        // a substring of the chrome device, e.g. 'Default - Microphone (Webcam Audio) (3443:60bb)' == 'Microphone (Webcam Audio)'
        const initialMatches = candidates.filter((candidate: any) => {
            const candidateLabel = candidate.label || candidate.name
            return chromeDevice.label.indexOf(candidateLabel) >= 0
        })

        if (initialMatches.length === 1) {
            return [initialMatches[0], null]
        }

        if (initialMatches.length === 0) {
            return this.matchChromeDeviceByRules(chromeDevice, candidates)
        }

        // if more than 1 matches:
        // apply a bit stricter subset matching where the word boundary is taken into account --
        // e.g. prevents 'Pro Tools Audio Bridge 6' matching with 'Pro Tools Audio Bridge 64'.
        const matches2 = initialMatches.filter((candidate: any) => {
            const candidateLabel = candidate.label || candidate.name
            const si = chromeDevice.label.indexOf(candidateLabel) - 1
            const ei = si + 1 + candidateLabel.length
            const sc = chromeDevice.label.charAt(si)
            const ec = chromeDevice.label.charAt(ei)
            return (sc === '' || sc === ' ') && (ec === ' ' || ec === '')
        })

        if (matches2.length === 0) {
            log.error('chromeDeviceLabelMatching', `noMatches: at checkpoint A`, {
                chromeDevice,
                candidates: toLablesOnly(candidates),
            })
            return [null, null]
        } else if (matches2.length === 1) {
            return [matches2[0], null]
        } else {
            // no choice but to guess.
            log.error('chromeDeviceLabelMatching', `multipleMatches: at checkpoint B`, {
                chromeDevice,
                matches2: toLablesOnly(matches2),
            })
            return [matches2[0], '']
        }
    }

    private matchChromeDeviceByRules<T extends NativeAudioDevice | AudioEngineAudioDeviceInfo>(
        chromeDevice: MediaDeviceInfo,
        candidates: T[]
    ): [T | null, string | null] {
        // find rule which matches the chrome device
        let matchingRules = this.rules.filter((rule) => {
            return chromeDevice.label.match(rule.chrome)
        })

        if (matchingRules.length === 0) {
            log.error('chromeDeviceLabelMatching', `noMatches: at checkpoint C`, {
                chromeDevice,
                candidates: toLablesOnly(candidates),
                rules: this.ruleStrings,
            })
            return [null, '']
        }

        if (matchingRules.length > 1) {
            log.error('chromeDeviceLabelMatching', `multipleMatchingRules: found multiple matching rules for device.`, {
                chromeDevice,
                candidates: toLablesOnly(candidates),
                matchingRules: toPrintable(matchingRules),
            })
        }

        for (let candidateRule of matchingRules[0].candidates) {
            let matches = candidates.filter((candidate: any) => {
                let candidateLabel = candidate.label || candidate.name
                return candidateLabel.match(candidateRule)
            })

            if (matches.length === 0) {
                continue // try the next candidateRule
            } else if (matches.length === 1) {
                return [matches[0], null]
            } else if (matches.length > 1) {
                log.error('chromeDeviceLabelMatching', `multipleMatches: at checkpoint D`, {
                    chromeDevice,
                    matches: toLablesOnly(matches),
                    candidates: toLablesOnly(candidates),
                    matchingRules: toPrintable(matchingRules),
                })
                return [matches[0], '']
            }
        }

        log.error('chromeDeviceLabelMatching', `noMatches: at checkpoint E`, {
            chromeDevice,
            candidates: toLablesOnly(candidates),
            matchingRules: toPrintable(matchingRules),
        })
        return [null, '']
    }
}

function toLablesOnly<T extends NativeAudioDevice | AudioEngineAudioDeviceInfo>(ds: T[]) {
    return ds.map((d) => {
        if ('audioEngineDeviceId' in d) {
            return d.audioEngineDeviceId
        } else {
            return d.label
        }
    })
}

function toPrintable(rules: Rule[]) {
    return rules.map((a) => ({ chrome_str: a.chrome_str, candidates_str: a.candidates_str }))
}
